import {
  FirestoreDataConverter,
  DocumentSnapshot,
  SnapshotOptions,
  WithFieldValue,
} from "firebase/firestore";
import { Expense, normalizeDiscountType, normalizeDiscountValue } from "../expenses";

// Helper to convert Date to ISO string in US/New York timezone
export const dateToNYIsoString = (date: Date | null): string | null => {
  if (!date) return null;
  // Format date to ISO string in US/New York timezone
  return new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' })).toISOString();
};

/** Normalize a list of attachment rotations to the nearest 0/90/180/270 degree value. */
const sanitizeAttachmentRotations = (value: unknown): number[] | null => {
  if (!Array.isArray(value)) return null;
  const out: number[] = [];
  for (const r of value) {
    if (typeof r === 'number' && Number.isFinite(r)) {
      out.push((((Math.round(r / 90) * 90) % 360) + 360) % 360);
    }
  }
  return out;
};


const expenseConverter: FirestoreDataConverter<Expense> = {
  toFirestore(expense: WithFieldValue<Expense>) {
    const { id, ...data } = expense;
    
    // Convert Date objects to ISO strings for Firestore storage
    const firestoreData = {
      ...data,
      paid: data.paid === true,
      creditCardCharge:
        typeof data.creditCardCharge === 'number' && !Number.isNaN(data.creditCardCharge)
          ? data.creditCardCharge
          : null,
      discountType: normalizeDiscountType(data.discountType),
      discountValue: normalizeDiscountValue(
        typeof data.discountValue === 'number' ? data.discountValue : null,
        normalizeDiscountType(data.discountType),
      ),
      paidAt:
        data.paid === true && data.paidAt instanceof Date
          ? dateToNYIsoString(data.paidAt)
          : null,
      date: data.date instanceof Date ? dateToNYIsoString(data.date) : data.date,
      created_at: data.created_at instanceof Date ? dateToNYIsoString(data.created_at) : data.created_at,
      updated_at: data.updated_at instanceof Date ? dateToNYIsoString(data.updated_at) : data.updated_at,
      // Ensure vehicleId is properly stored
      vehicleId: data.vehicleId || null,
      transportCompanyId: data.transportCompanyId || null,
      // Ensure new fields are properly stored
      invoiceNumber: data.invoiceNumber || null,
      vendorCategory: data.vendorCategory || null,
      businessName: data.businessName || "",
      repairSubcategories: Array.isArray(data.repairSubcategories) ? data.repairSubcategories : null,
      customRepairSubcategory: data.customRepairSubcategory || null,
      repairPartsAmount: typeof data.repairPartsAmount === 'number' ? data.repairPartsAmount : null,
      repairLaborAmount: typeof data.repairLaborAmount === 'number' ? data.repairLaborAmount : null,
      repairFeesAmount: typeof data.repairFeesAmount === 'number' ? data.repairFeesAmount : null,
      repairTaxAmount: typeof data.repairTaxAmount === 'number' ? data.repairTaxAmount : null,
      repairShopId: data.repairShopId || null,
      repairTruckId: data.repairTruckId || null,
      sourceRepairInvoiceStoragePath: data.sourceRepairInvoiceStoragePath || null,
      sourceRepairTruckId: data.sourceRepairTruckId || null,
      partName: data.partName || null,
      partNumber: data.partNumber || null,
      orderNumber: data.orderNumber || null,
      listPrice: typeof data.listPrice === 'number' && !Number.isNaN(data.listPrice) ? data.listPrice : null,
      partCost: typeof data.partCost === 'number' && !Number.isNaN(data.partCost) ? data.partCost : null,
      shippingCost: typeof data.shippingCost === 'number' && !Number.isNaN(data.shippingCost) ? data.shippingCost : null,
      reimbursementSubcategory: data.reimbursementSubcategory || null,
      attachmentPaths: Array.isArray(data.attachmentPaths)
        ? data.attachmentPaths.filter((path): path is string => typeof path === "string" && path.length > 0)
        : null,
      attachmentURLs: Array.isArray(data.attachmentURLs)
        ? data.attachmentURLs.filter((url): url is string => typeof url === "string" && url.length > 0)
        : null,
      attachmentRotations: sanitizeAttachmentRotations(data.attachmentRotations),
      // AI invoice import / review fields
      reviewStatus: data.reviewStatus || null,
      awaitingExtraction: data.awaitingExtraction === true,
      extractedData: data.extractedData ?? null,
      extractionModel: data.extractionModel || null,
      extractionError: data.extractionError || null,
      suggestedCategory: data.suggestedCategory || null,
      lineItems: Array.isArray(data.lineItems) ? data.lineItems : null,
      plannedPaymentWindow:
        data.plannedPaymentWindow === 'today' || data.plannedPaymentWindow === 'this_week'
          ? data.plannedPaymentWindow
          : null,
    };
    return firestoreData;
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): Expense {
    const data = snapshot.data(options);
    
    if (!data)
      return {
        id: snapshot.id,
        category: "Other",
        amount: 0,
        paid: false,
        creditCardCharge: null,
        discountType: null,
        discountValue: null,
        paidAt: null,
        description: "",
        date: new Date(),
        created_by: "",
        created_at: new Date(),
        updated_at: null,
        updated_by: null,
        customCategory: null,
        vehicleId: null,
        transportCompanyId: null,
        invoiceNumber: null,
        vendorCategory: null,
        businessName: null,
        attachmentPath: null,
        attachmentURL: null,
        attachmentPaths: null,
        attachmentURLs: null,
        attachmentRotations: null,
        repairSubcategories: null,
        customRepairSubcategory: null,
        repairPartsAmount: null,
        repairLaborAmount: null,
        repairFeesAmount: null,
        repairTaxAmount: null,
        repairShopId: null,
        repairTruckId: null,
        sourceRepairInvoiceStoragePath: null,
        sourceRepairTruckId: null,
        partName: null,
        partNumber: null,
        orderNumber: null,
        listPrice: null,
        partCost: null,
        shippingCost: null,
        reimbursementSubcategory: null,
        reviewStatus: null,
        awaitingExtraction: null,
        extractedData: null,
        extractionModel: null,
        extractionError: null,
        suggestedCategory: null,
        lineItems: null,
        plannedPaymentWindow: null,
      };

    // Convert numeric values safely
    const safeNumber = (value: any): number => {
      if (value === undefined || value === null) return 0;
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };
    
    // Parse date strings, Timestamps, or Date objects to Date objects
    const parseDate = (dateValue: any): Date => {
      if (!dateValue) return new Date();
      try {
        // Handle Firestore Timestamp
        if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
          return dateValue.toDate();
        }
        // Handle Date objects
        if (dateValue instanceof Date) {
          return dateValue;
        }
        // Handle ISO strings
        if (typeof dateValue === 'string') {
          return new Date(dateValue);
        }
        return new Date();
      } catch (e) {
        return new Date();
      }
    };

    const parseDateNullable = (dateValue: unknown): Date | null => {
      if (dateValue === undefined || dateValue === null) return null;
      try {
        if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
          return (dateValue as { toDate: () => Date }).toDate();
        }
        if (dateValue instanceof Date) {
          return dateValue;
        }
        if (typeof dateValue === 'string') {
          const d = new Date(dateValue);
          return Number.isNaN(d.getTime()) ? null : d;
        }
      } catch {
        return null;
      }
      return null;
    };
    
    // Keep any non-empty category string (built-in or custom); default to "Other"
    const category =
      typeof data.category === "string" && data.category.length > 0 ? data.category : "Other";

    return {
      id: snapshot.id,
      category: category,
      amount: safeNumber(data.amount),
      paid: data.paid === true,
      creditCardCharge: typeof data.creditCardCharge === 'number' && !Number.isNaN(data.creditCardCharge) ? data.creditCardCharge : null,
      discountType: normalizeDiscountType(data.discountType),
      discountValue: normalizeDiscountValue(
        typeof data.discountValue === 'number' ? data.discountValue : null,
        normalizeDiscountType(data.discountType),
      ),
      paidAt: data.paid === true ? parseDateNullable(data.paidAt) : null,
      description: data.description || "",
      date: parseDate(data.date),
      created_by: data.created_by || "",
      created_at: parseDate(data.created_at),
      updated_at: data.updated_at ? parseDate(data.updated_at) : null,
      updated_by: data.updated_by || null,
      customCategory: category === "Other" ? (data.customCategory || null) : null,
      vehicleId: data.vehicleId || null,
      transportCompanyId: data.transportCompanyId || null,
      // Add new fields with defaults
      invoiceNumber: data.invoiceNumber || null,
      vendorCategory: data.vendorCategory || null,
      businessName: data.businessName || null,
      attachmentPath: data.attachmentPath || null,
      attachmentURL: data.attachmentURL || null,
      attachmentPaths: Array.isArray(data.attachmentPaths)
        ? data.attachmentPaths.filter((path: unknown): path is string => typeof path === 'string' && path.length > 0)
        : null,
      attachmentURLs: Array.isArray(data.attachmentURLs)
        ? data.attachmentURLs.filter((url: unknown): url is string => typeof url === 'string' && url.length > 0)
        : null,
      attachmentRotations: sanitizeAttachmentRotations(data.attachmentRotations),
      repairSubcategories: Array.isArray(data.repairSubcategories) ? data.repairSubcategories : null,
      customRepairSubcategory: data.customRepairSubcategory || null,
      repairPartsAmount: typeof data.repairPartsAmount === 'number' ? data.repairPartsAmount : null,
      repairLaborAmount: typeof data.repairLaborAmount === 'number' ? data.repairLaborAmount : null,
      repairFeesAmount: typeof data.repairFeesAmount === 'number' ? data.repairFeesAmount : null,
      repairTaxAmount: typeof data.repairTaxAmount === 'number' ? data.repairTaxAmount : null,
      repairShopId: data.repairShopId || null,
      repairTruckId: data.repairTruckId || null,
      sourceRepairInvoiceStoragePath: data.sourceRepairInvoiceStoragePath || null,
      sourceRepairTruckId: data.sourceRepairTruckId || null,
      partName: data.partName || null,
      partNumber: data.partNumber || null,
      orderNumber: data.orderNumber || null,
      listPrice: typeof data.listPrice === 'number' && !Number.isNaN(data.listPrice) ? data.listPrice : null,
      partCost: typeof data.partCost === 'number' && !Number.isNaN(data.partCost) ? data.partCost : null,
      shippingCost: typeof data.shippingCost === 'number' && !Number.isNaN(data.shippingCost) ? data.shippingCost : null,
      reimbursementSubcategory: data.reimbursementSubcategory || null,
      // AI invoice import / review fields
      reviewStatus: data.reviewStatus || null,
      awaitingExtraction: data.awaitingExtraction === true ? true : null,
      extractedData: (data.extractedData as Record<string, unknown> | null) ?? null,
      extractionModel: data.extractionModel || null,
      extractionError: data.extractionError || null,
      suggestedCategory: data.suggestedCategory || null,
      lineItems: Array.isArray(data.lineItems)
        ? (data.lineItems as Array<Record<string, unknown>>).map(li => ({
            description: typeof li.description === 'string' ? li.description : '',
            amount: typeof li.amount === 'number' ? li.amount : null,
            quantity: typeof li.quantity === 'number' && Number.isFinite(li.quantity) ? li.quantity : null,
            subcategory: typeof li.subcategory === 'string' && li.subcategory.length > 0 ? li.subcategory : null,
            repairGroup: typeof li.repairGroup === 'string' ? li.repairGroup : null,
          }))
        : null,
      plannedPaymentWindow:
        data.plannedPaymentWindow === 'today' || data.plannedPaymentWindow === 'this_week'
          ? data.plannedPaymentWindow
          : null,
    };
  },
};

export { expenseConverter }; 